import {
  Box,
  Typography,
  Link,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Dispatch, Fragment, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { findAncestorPendingSnapshotDataPackage } from '@/lib/tree/findAncestorDataPackageContext';
import { FormTextField, FormSelect } from '@/components/common/FormComponents';
import {
  FileMetaDataCreateOrUpdateRequest,
  FileMetaDataDto,
  FileDto,
  FileMetaDataFieldDto,
  FileMetaDataFieldWriteRequest,
  FileParameter,
  PrivilegeEnum,
  StatusDto,
  VisibilityDto,
  ActivityCategoryDto,
} from '@/api/ApiClient';
import { useBackendStore } from '@/stores/backendStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { getVisibilityTypeDisplay, visibilitySelectOptions } from '@/lib/types/visibilityTypes';
import { TreeNode } from '@/types/tree';
import { useTree } from '@/hooks/useTree';
import { FileOperationConfigEntry, useFileOperationConfig } from '@/hooks/useFileOperationConfig';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { joinApiEndpointPath } from '@/lib/utils/urlUtils';

import { nodeIsReadOnlyForUi } from '@/lib/tree/treeNodeTypeConfigs';
import { usePrivileges } from '@/hooks/usePrivileges';
import { useAuthStore } from '@/stores/authStore';
import { useDialogStore } from '@/stores/dialogStore';
import { canCreateFile, canUpdateFile, canDeleteFile } from '@/types/tree/actions/noteFilePrivilegeHelpers';
import { canMutateFilesOnParent, getFileParentEntityData } from '@/types/tree/actions/fileParentPrivilegeHelpers';
import { isFilesUploadDisabled } from '@/types/tree/actions/filesTabAuth';
import {
  nodeTypeToFileParentKind,
  parentStatusAllowsFileMutation,
} from '@/types/tree/actions/fileMutationStatusPolicy';

interface Props {
  files?: FileMetaDataDto[];
  node: TreeNode;
}

interface UploadStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

interface UploadSingleFileParams {
  fileIndex: number;
  file: File;
  dataId: string;
  getFileConfig: () => FileOperationConfigEntry;
  setUploadStatuses: Dispatch<SetStateAction<UploadStatus[]>>;
  addFiles: (dataId: string, newFiles: FileMetaDataDto[]) => void;
  /** Clear a previous error when re-entering uploading (retry path). */
  clearPreviousError?: boolean;
}

/** Upload one file and update progress state; returns whether the upload succeeded. */
async function uploadSingleFile({
  fileIndex,
  file,
  dataId,
  getFileConfig,
  setUploadStatuses,
  addFiles,
  clearPreviousError = false,
}: UploadSingleFileParams): Promise<boolean> {
  setUploadStatuses((prev) =>
    prev.map((status, index) =>
      index === fileIndex
        ? {
            ...status,
            status: 'uploading',
            progress: 0,
            ...(clearPreviousError ? { error: undefined } : {}),
          }
        : status
    )
  );

  const fileParameter: FileParameter = {
    data: file,
    fileName: file.name,
  };

  try {
    const config = getFileConfig();
    const uploaded = await config.upload(fileParameter);

    setUploadStatuses((prev) =>
      prev.map((status, index) => (index === fileIndex ? { ...status, status: 'success', progress: 100 } : status))
    );
    addFiles(dataId, [uploaded]);
    return true;
  } catch (error) {
    log.debug('[FilesDetails] Upload failed:', error);
    const errorMessage = getProblemDetail(error) ?? 'Upload failed';

    setUploadStatuses((prev) =>
      prev.map((status, index) =>
        index === fileIndex ? { ...status, status: 'error', progress: 0, error: errorMessage } : status
      )
    );
    return false;
  }
}

/** Matches Knockout file table column order (configured field order, then label). */
function sortFileMetadataFields(fields: FileMetaDataFieldDto[]): FileMetaDataFieldDto[] {
  return [...fields].sort((a, b) => {
    const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base' });
  });
}

/** One set of metadata column headers for the whole table (union of fields, stable order). */
function buildSharedMetadataColumnLabels(files: FileMetaDataDto[]): string[] {
  const labelToOrder = new Map<string, number>();
  for (const file of files) {
    for (const f of sortFileMetadataFields(file.fields ?? [])) {
      const label = f.label?.trim() ?? '';
      if (!label) {
        continue;
      }
      const o = f.order ?? Number.MAX_SAFE_INTEGER;
      const prev = labelToOrder.get(label);
      if (prev === undefined || o < prev) {
        labelToOrder.set(label, o);
      }
    }
  }
  return Array.from(labelToOrder.entries())
    .sort((a, b) => {
      if (a[1] !== b[1]) {
        return a[1] - b[1];
      }
      return a[0].localeCompare(b[0], undefined, { sensitivity: 'base' });
    })
    .map(([label]) => label);
}

function getMetadataValueForLabel(file: FileMetaDataDto, columnLabel: string): string {
  const needle = columnLabel.toLowerCase();
  const match = file.fields?.find((f) => (f.label ?? '').toLowerCase() === needle);
  return match?.value ?? '';
}

function fileHasDescriptionField(file: FileMetaDataDto): boolean {
  return (file.fields ?? []).some((f) => f.label.toLowerCase() === 'description');
}

function findFieldByLabel(file: FileMetaDataDto, columnLabel: string): FileMetaDataFieldDto | undefined {
  const needle = columnLabel.toLowerCase();
  return file.fields?.find((f) => (f.label ?? '').toLowerCase() === needle);
}

export const FilesDetails = ({ files = [], node }: Props) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatuses, setUploadStatuses] = useState<UploadStatus[]>([]);
  const systemConfig = useSystemConfig();
  const backend = useBackendStore((s) => s.getBackend());
  const { showSnackbar } = useSnackbar();
  const { showConfirmDialog } = useDialogStore();
  const { isAuthorized } = usePrivileges();
  const currentUser = useAuthStore((s) => s.currentUser?.id);

  // File privilege checks
  const canCreate = canCreateFile(isAuthorized);
  const {
    getTypedData,
    getNodeData,
    getSampleData,
    getSampleActivityData,
    getProjectActivityData,
    getTheoryActivityData,
    getShippingData,
    getDataPackageData,
    getCommunicationData,
    reloadNodeData,
    addFiles,
    replaceFile,
    loadFiles,
  } = useTree();
  const isReadOnlyNode = nodeIsReadOnlyForUi(node.type);
  const { getFileConfig } = useFileOperationConfig(node);
  const { resolveUser } = useUserResolution('all');

  const fileParent = getFileParentEntityData(node, {
    getSampleData,
    getSampleActivityData,
    getProjectActivityData,
    getTheoryActivityData,
    getShippingData,
    getDataPackageData,
    getCommunicationData,
  });
  const nodeStatus = fileParent?.status;
  const canMutateParent = canMutateFilesOnParent(node.type, fileParent, isAuthorized);

  const parentKind = nodeTypeToFileParentKind(node.type);
  const parentStatusBlocksFiles =
    nodeStatus === StatusDto.Published ||
    (parentKind != null && !parentStatusAllowsFileMutation(parentKind, nodeStatus));

  const uploadDisabled = isFilesUploadDisabled({
    nodeType: node.type,
    nodeStatus,
    isReadOnlyNode,
    canCreateFile: canCreate,
    canMutateFilesOnParent: canMutateParent,
  });

  const uploadDisabledTooltip =
    parentKind === 'dataPackage' && nodeStatus !== StatusDto.Pending
      ? 'Files can only be uploaded to pending data packages'
      : parentStatusBlocksFiles
        ? 'Cannot upload files in this status'
        : 'Upload one or more files';

  /** Parent privilege + status policy; blocks attach/edit/delete (not snapshot trim). */
  const filesMutationLocked = !canMutateParent;

  /** Pending completed snapshot package above this node (trim snapshot-only files). */
  const snapshotPackageContext = useMemo(() => {
    if (node.type !== 'sampleActivitySnapshot') {
      return null;
    }
    return findAncestorPendingSnapshotDataPackage(node, getNodeData, getTypedData);
  }, [node, getNodeData, getTypedData]);

  const sampleActivity =
    node.type === 'sampleActivity' ||
    node.type === 'sampleActivityInPackage' ||
    node.type === 'sampleActivitySnapshot' ||
    node.type === 'sampleActivityAggregate'
      ? getSampleActivityData(node)
      : null;
  const isProcessingPackagedActivity =
    node.type === 'sampleActivitySnapshot' && sampleActivity?.category === ActivityCategoryDto.Processing;
  const canTrimSnapshotActivityFiles = isAuthorized(PrivilegeEnum.DataPackage, 'update');
  const showSnapshotFileDeleteUi =
    node.type === 'sampleActivitySnapshot' &&
    snapshotPackageContext !== null &&
    !isProcessingPackagedActivity &&
    canTrimSnapshotActivityFiles;

  // RO nodes normally have no actions column; snapshot trim is the exception (pending package remove).
  const showFileActionsColumn = !isReadOnlyNode || showSnapshotFileDeleteUi;

  const [editingFileId, setEditingFileId] = useState<number | null>(null);
  const [editName, setEditName] = useState<string>('');
  const [editVisibility, setEditVisibility] = useState<string>('');
  const [editFieldValues, setEditFieldValues] = useState<Record<string, string>>({});
  const [editTopDescription, setEditTopDescription] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const metadataColumnLabels = useMemo(() => buildSharedMetadataColumnLabels(files), [files]);

  // Clear successful uploads after 3 seconds, but keep failed uploads visible
  useEffect(() => {
    if (uploadStatuses.length === 0) return;

    // Check if there are any successful uploads
    const successfulUploads = uploadStatuses.filter((status) => status.status === 'success');
    if (successfulUploads.length === 0) return;

    // Check if all uploads are complete (no pending or uploading)
    const allComplete = uploadStatuses.every((status) => status.status === 'success' || status.status === 'error');
    if (!allComplete) return;

    // Clear only successful uploads after 3 seconds, keep failed ones
    const timeoutId = setTimeout(() => {
      setUploadStatuses((currentStatuses) => {
        // Remove only successful uploads, keep errors
        const remaining = currentStatuses.filter((status) => status.status !== 'success');
        if (remaining.length === 0) {
          setIsUploading(false);
        }
        return remaining;
      });
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [uploadStatuses]);

  const handleRetryUpload = async (fileIndex: number) => {
    const uploadStatus = uploadStatuses[fileIndex];
    if (!uploadStatus || uploadStatus.status !== 'error') return;

    const success = await uploadSingleFile({
      fileIndex,
      file: uploadStatus.file,
      dataId: node.dataId,
      getFileConfig,
      setUploadStatuses,
      addFiles,
      clearPreviousError: true,
    });

    if (success) {
      await reloadNodeData(node.nodeId, backend);
      showSnackbar(`Successfully uploaded: ${uploadStatus.file.name}`, 'success');
    } else {
      showSnackbar(`Retry failed for: ${uploadStatus.file.name}`, 'error');
    }
  };

  const handleFileUpload = async (files: File[]) => {
    if (!files || files.length === 0 || !node) return;

    // Initialize upload statuses for all files
    const initialStatuses: UploadStatus[] = files.map((file) => ({
      file,
      status: 'pending',
      progress: 0,
    }));
    setUploadStatuses(initialStatuses);
    setIsUploading(true);

    let successCount = 0;
    let errorCount = 0;

    // Upload files sequentially to avoid overwhelming the server
    for (let i = 0; i < files.length; i++) {
      const success = await uploadSingleFile({
        fileIndex: i,
        file: files[i],
        dataId: node.dataId,
        getFileConfig,
        setUploadStatuses,
        addFiles,
      });
      if (success) {
        successCount++;
      } else {
        errorCount++;
      }
    }

    // Reload entity data only if any uploads succeeded (files already in store; entity metadata may have changed)
    if (successCount > 0) {
      await reloadNodeData(node.nodeId, backend);
    }

    // Show summary message
    if (successCount > 0 && errorCount === 0) {
      showSnackbar(`Successfully uploaded ${successCount} file(s)`, 'success');
    } else if (successCount > 0 && errorCount > 0) {
      showSnackbar(
        `Uploaded ${successCount} file(s), ${errorCount} failed. Use retry buttons to retry failed uploads.`,
        'warning'
      );
    } else {
      showSnackbar(`Failed to upload ${errorCount} file(s). Use retry buttons to retry failed uploads.`, 'error');
    }

    setIsUploading(false);

    // Note: Successful uploads will be cleared automatically by useEffect after 3 seconds
    // Failed uploads will remain visible for user to retry
  };

  const handleSaveMetadata = async (file: FileMetaDataDto) => {
    if (!node) return;
    setIsSaving(true);
    try {
      const visibility = editVisibility ? (editVisibility as VisibilityDto) : undefined;
      const fieldWrites: FileMetaDataFieldWriteRequest[] = metadataColumnLabels.map((label, idx) => {
        const orig = findFieldByLabel(file, label);
        return {
          label,
          type: orig?.type,
          value: editFieldValues[label] ?? '',
          order: orig?.order ?? idx,
        };
      });

      const descLabel = metadataColumnLabels.find((l) => l.toLowerCase() === 'description');
      const request: FileMetaDataCreateOrUpdateRequest = {
        id: file.id,
        basename: editName,
        visibility,
        description: descLabel ? (editFieldValues[descLabel] ?? '') : editTopDescription,
        fields: fieldWrites.length > 0 ? fieldWrites : undefined,
      };

      const config = getFileConfig();
      const updated = await config.update(request);
      replaceFile(node.dataId, updated);
      await reloadNodeData(node.nodeId, backend);

      showSnackbar(`File "${editName}" saved successfully`, 'success');
      setEditingFileId(null);
    } catch (error) {
      log.debug('[FilesDetails] Update failed:', error);
      showSnackbar(`Failed to save "${editName}"`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Deletes file attachment(s): for a pending snapshot packaged activity, removes the whole logical group via
   * `deleteSnapshotFileGroup` (metadata id) after a destructive-action confirm (snapshot-only; originals unchanged).
   * Otherwise deletes one physical row if {@link physicalFile} is set, or every stored format in the group when it
   * is omitted (matches legacy Knockout `removeFiles`).
   */
  const handleDeleteFile = async (file: FileMetaDataDto, physicalFile?: FileDto) => {
    if (!node) return;

    const config = getFileConfig();
    const displayName = physicalFile?.filename ?? file.basename ?? '';

    try {
      if (config.deleteSnapshotFileGroup) {
        const groupId = file.id;
        if (groupId == null) {
          log.debug('[FilesDetails] Missing file metadata id for snapshot trim:', { file });
          showSnackbar('File metadata id is missing. Cannot remove from snapshot.', 'error');
          return;
        }
        const confirmed = await showConfirmDialog({
          title: 'Remove from snapshot?',
          message:
            'This permanently removes the file from the snapshot copy only. The original sample and its source files are not changed.\n\nDo you want to continue?',
          confirmText: 'Continue',
          cancelText: 'Cancel',
          isDestructive: true,
        });
        if (!confirmed) {
          return;
        }
        await config.deleteSnapshotFileGroup(groupId);
      } else {
        const targets = physicalFile != null ? [physicalFile] : (file.files ?? []);
        if (targets.length === 0) {
          log.debug('[FilesDetails] No physical file rows to delete:', { file, physicalFile });
          showSnackbar('File ID is missing. Cannot delete.', 'error');
          return;
        }
        for (const pf of targets) {
          const physicalId = pf.id;
          if (physicalId == null) {
            log.debug('[FilesDetails] Missing physical file id for deletion:', { file, physicalFile: pf });
            showSnackbar('File ID is missing. Cannot delete.', 'error');
            return;
          }
          await config.delete(physicalId);
        }
      }

      await loadFiles(node.dataId, backend);
      await reloadNodeData(node.nodeId, backend);
      showSnackbar(`File ${displayName} deleted successfully`, 'success');
    } catch (error) {
      log.debug('[FilesDetails] Failed to delete file:', error);
      showSnackbar(`Failed to delete "${displayName}"`, 'error');
    }
  };

  return (
    <Box>
      {!isReadOnlyNode && canCreate ? (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Tooltip title={uploadDisabled ? uploadDisabledTooltip : 'Upload one or more files'}>
            <span>
              <Button
                variant="contained"
                component="label"
                startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
                disabled={isUploading || uploadDisabled}
                sx={{
                  backgroundColor: isUploading || uploadDisabled ? 'grey.400' : undefined,
                  '&:hover': {
                    backgroundColor: isUploading || uploadDisabled ? 'grey.500' : undefined,
                  },
                }}
              >
                Upload Files
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) handleFileUpload(files);
                    e.target.value = ''; // allow re-selecting the same files
                  }}
                />
              </Button>
            </span>
          </Tooltip>
        </Box>
      ) : null}

      {/* Upload Progress Section */}
      {uploadStatuses.length > 0 ? (
        <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Upload Progress</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" color="text.secondary">
                {uploadStatuses.filter((s) => s.status === 'success').length} of {uploadStatuses.length} completed
              </Typography>
              {uploadStatuses.some((s) => s.status === 'error') ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    const failedIndices = uploadStatuses
                      .map((status, index) => (status.status === 'error' ? index : -1))
                      .filter((index) => index !== -1);
                    failedIndices.forEach((index) => handleRetryUpload(index));
                  }}
                >
                  Retry All Failed
                </Button>
              ) : null}
            </Box>
          </Box>
          <List dense>
            {uploadStatuses.map((uploadStatus, index) => (
              <ListItem key={index} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {uploadStatus.status === 'success' ? <CheckCircleIcon color="success" /> : null}
                  {uploadStatus.status === 'error' ? <ErrorIcon color="error" /> : null}
                  {uploadStatus.status === 'uploading' ? <CircularProgress size={20} /> : null}
                  {uploadStatus.status === 'pending' ? (
                    <CircularProgress size={20} variant="determinate" value={0} />
                  ) : null}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>
                        {uploadStatus.file.name}
                      </Typography>
                      <Chip
                        label={uploadStatus.status}
                        size="small"
                        color={
                          uploadStatus.status === 'success'
                            ? 'success'
                            : uploadStatus.status === 'error'
                              ? 'error'
                              : uploadStatus.status === 'uploading'
                                ? 'primary'
                                : 'default'
                        }
                        variant="outlined"
                      />
                      {uploadStatus.status === 'error' ? (
                        <IconButton
                          size="small"
                          onClick={() => handleRetryUpload(index)}
                          sx={{ ml: 1, p: 0.5 }}
                          title="Retry upload"
                        >
                          <RefreshIcon fontSize="small" color="primary" />
                        </IconButton>
                      ) : null}
                    </Box>
                  }
                  secondary={
                    uploadStatus.status === 'uploading' ? (
                      <LinearProgress variant="indeterminate" sx={{ mt: 1 }} />
                    ) : uploadStatus.status === 'error' ? (
                      <Typography variant="caption" color="error">
                        {uploadStatus.error}
                      </Typography>
                    ) : null
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ) : null}

      {files.length === 0 ? (
        <Typography>No files available.</Typography>
      ) : (
        <TableContainer
          sx={{
            maxWidth: '100%',
            overflowX: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, minWidth: 160, verticalAlign: 'bottom' }}>File</TableCell>
                {metadataColumnLabels.map((label) => (
                  <TableCell key={label} sx={{ fontWeight: 700, whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                    {label}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700, verticalAlign: 'bottom' }}>Downloads</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                  Uploaded by
                </TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                  Visibility
                </TableCell>
                {showFileActionsColumn ? (
                  <TableCell align="right" sx={{ fontWeight: 700, verticalAlign: 'bottom', width: 96 }}>
                    &nbsp;
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map((file, idx) => {
                const canUpdate = canUpdateFile(isAuthorized, file, currentUser);
                const canDelete = canDeleteFile(isAuthorized, file, currentUser);
                const showNormalFileActions = !isReadOnlyNode && (canUpdate || canDelete);
                const showSnapshotDeleteOnly = showSnapshotFileDeleteUi;
                const showActions = showNormalFileActions || showSnapshotDeleteOnly;
                const editColSpan = 4 + metadataColumnLabels.length + (showFileActionsColumn ? 1 : 0);

                return (
                  <Fragment key={file.id ?? idx}>
                    <TableRow hover>
                      <TableCell
                        sx={{
                          verticalAlign: 'top',
                          maxWidth: 280,
                          wordBreak: 'break-word',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {file.basename}
                          </Typography>
                          {file.isRecipeSource ? (
                            <Chip
                              label="Recipe source"
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ maxWidth: '100%' }}
                            />
                          ) : null}
                          {!fileHasDescriptionField(file) && file.description ? (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {file.description}
                            </Typography>
                          ) : null}
                        </Box>
                      </TableCell>
                      {metadataColumnLabels.map((label) => (
                        <TableCell
                          key={label}
                          sx={{
                            verticalAlign: 'top',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {getMetadataValueForLabel(file, label)}
                        </TableCell>
                      ))}
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {(file.files ?? []).map((f, fidx) => (
                            <Button
                              key={fidx}
                              component={Link}
                              href={joinApiEndpointPath(systemConfig?.apiEndpoint, f.downloadUrl) || '#'}
                              target="_blank"
                              rel="noopener"
                              size="small"
                              variant="contained"
                              sx={{ textTransform: 'none', minWidth: 'auto', py: 0.25, px: 1 }}
                            >
                              {(f.filetype ?? '').toUpperCase()}
                            </Button>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                        {file.lastChangedByUser ? resolveUser(file.lastChangedByUser) : '—'}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                        {getVisibilityTypeDisplay(file.visibility ?? '')}
                      </TableCell>
                      {showActions ? (
                        <TableCell align="right" sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                          <Box display="inline-flex" gap={0.5} justifyContent="flex-end">
                            {showNormalFileActions && canUpdate ? (
                              <Tooltip
                                title={filesMutationLocked ? 'Cannot edit files in this status' : 'Edit file metadata'}
                              >
                                <span>
                                  <IconButton
                                    onClick={() => {
                                      if (filesMutationLocked) return;
                                      setEditingFileId(file.id ?? null);
                                      setEditName(file.basename ?? '');
                                      setEditVisibility(file.visibility ?? 'USER');
                                      setEditFieldValues(
                                        Object.fromEntries(
                                          metadataColumnLabels.map((l) => [l, getMetadataValueForLabel(file, l)])
                                        )
                                      );
                                      setEditTopDescription(
                                        fileHasDescriptionField(file) ? '' : (file.description ?? '')
                                      );
                                    }}
                                    size="small"
                                    disabled={filesMutationLocked}
                                    sx={{
                                      backgroundColor: filesMutationLocked ? 'grey.400' : 'primary.main',
                                      color: 'white',
                                      borderRadius: 1,
                                      width: 32,
                                      height: 32,
                                      '&:hover': {
                                        backgroundColor: filesMutationLocked ? 'grey.500' : 'primary.dark',
                                      },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : null}
                            {showNormalFileActions && canDelete ? (
                              <Tooltip
                                title={
                                  filesMutationLocked ? 'Cannot delete files in this status' : 'Delete uploaded file'
                                }
                              >
                                <span>
                                  <IconButton
                                    onClick={() => {
                                      if (filesMutationLocked) return;
                                      handleDeleteFile(file);
                                    }}
                                    size="small"
                                    disabled={filesMutationLocked}
                                    sx={{
                                      backgroundColor: filesMutationLocked ? 'grey.400' : 'error.main',
                                      color: 'white',
                                      borderRadius: 1,
                                      width: 32,
                                      height: 32,
                                      '&:hover': {
                                        backgroundColor: filesMutationLocked ? 'grey.500' : 'error.dark',
                                      },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : null}
                            {showSnapshotDeleteOnly ? (
                              <Tooltip title="Remove file from this snapshot (pending data package only)">
                                <span>
                                  <IconButton
                                    onClick={() => handleDeleteFile(file)}
                                    size="small"
                                    sx={{
                                      backgroundColor: 'error.main',
                                      color: 'white',
                                      borderRadius: 1,
                                      width: 32,
                                      height: 32,
                                      '&:hover': {
                                        backgroundColor: 'error.dark',
                                      },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : null}
                          </Box>
                        </TableCell>
                      ) : showFileActionsColumn ? (
                        <TableCell />
                      ) : null}
                    </TableRow>
                    {editingFileId === file.id ? (
                      <TableRow>
                        <TableCell colSpan={editColSpan} sx={{ backgroundColor: 'action.hover', py: 2 }}>
                          <Box display="flex" flexDirection="column" gap={2} maxWidth={960}>
                            {file.isRecipeSource ? (
                              <Chip
                                label="Recipe source"
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ alignSelf: 'flex-start' }}
                              />
                            ) : null}
                            <FormTextField
                              label="File Name"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              fullWidth
                            />
                            <FormSelect
                              label="Visibility"
                              value={editVisibility}
                              onChange={(e) => setEditVisibility(e.target.value as string)}
                              options={visibilitySelectOptions()}
                              size="small"
                            />
                            {!fileHasDescriptionField(file) ? (
                              <FormTextField
                                label="Description"
                                value={editTopDescription}
                                onChange={(e) => setEditTopDescription(e.target.value)}
                                fullWidth
                                multiline
                                minRows={2}
                              />
                            ) : null}
                            {metadataColumnLabels.length > 0 ? (
                              <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }} gap={2}>
                                {metadataColumnLabels.map((label) => (
                                  <FormTextField
                                    key={label}
                                    label={label}
                                    value={editFieldValues[label] ?? ''}
                                    onChange={(e) =>
                                      setEditFieldValues((prev) => ({ ...prev, [label]: e.target.value }))
                                    }
                                    fullWidth
                                  />
                                ))}
                              </Box>
                            ) : null}
                            <Box display="flex" justifyContent="flex-end" gap={2}>
                              <Button onClick={() => setEditingFileId(null)}>Cancel</Button>
                              <Button variant="contained" onClick={() => handleSaveMetadata(file)} disabled={isSaving}>
                                Save Changes
                              </Button>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
